'use client';
import {
  IMessagePayload,
  useMessageContext,
  useConversationContext,
  useToast,
} from '@ui/components';
import { sendGAEvent } from '@next/third-parties/google';
import { BasePostCreationForm } from './base-post-creation-form';
import type { IPollDraftServerError } from './common/message-poll-draft-editor';
import { readPollInvalidError } from '@polls/message-poll';
import { useState } from 'react';

interface PostFormProps {
  creatorData?: { platform_settings?: { enable_video_uploads?: boolean } };
}

export const PostForm = ({ creatorData }: PostFormProps) => {
  const toast = useToast();
  const messageState = useMessageContext('creator');
  const conversationState = useConversationContext();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (
    message: IMessagePayload,
    description: string,
    mentions: string[],
    clearEditorContent: () => void,
    clearMessage: () => void,
    discardFiles: (opts?: { revoke?: boolean }) => void,
    showPollInvalid?: (error: IPollDraftServerError) => void,
  ) => {
    try {
      setIsSubmitting(true);
      const res = await messageState.onCreateMessage({
        message: {
          ...message,
          description,
          creator_id: conversationState.creator_id,
          thread_id: messageState.query.thread_id,
          user_id: conversationState.creator_id,
          mentions,
          created_by_creator: true,
        },
        // Do not clear in onOptimisticUpdate: retain content if POST or job fails.
        onOptimisticUpdate: () => {},
      });
      if (res?.data?.message?.id) {
        clearMessage();
        discardFiles();
        clearEditorContent();
        // Send GA Event for Successful Post
        sendGAEvent('event', 'CreatePostSuccessful', {
          event_label: 'Creator Post success',
          creatorId: conversationState.creator_id,
          postId: res?.data.message?.id,
        });
        // create post thread
        await messageState.onCreateMessageThread({
          message_id: res?.data?.message?.id,
          conversation_id: res?.data?.message_thread?.conversation_id,
          queryParams: messageState.query,
        });
      } else {
        const pollInvalid = readPollInvalidError((res as any)?.error);
        if (pollInvalid && showPollInvalid) showPollInvalid(pollInvalid);
        else toast({ title: 'Error creating post, please try again' }, { type: 'error' });
      }
    } catch (e) {
      const pollInvalid = readPollInvalidError(e);
      if (pollInvalid && showPollInvalid) {
        showPollInvalid(pollInvalid);
        return;
      }
      toast({ title: 'Error creating post, please try again' }, { type: 'error' });
      // Send GA Event for failed Post
      sendGAEvent('event', 'CreatePostFailed', {
        creatorId: conversationState.creator_id,
        event_label: 'Creator Post Failed',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <BasePostCreationForm
      onSubmit={onSubmit}
      authorAvatarURL={conversationState.userAvatarURL}
      userType="creator"
      currentUser={messageState.isCreator ? 'creator' : 'fan'}
      isLoading={messageState.isCreating || messageState.isPostingImage || isSubmitting}
      isSubmitDisabled={isSubmitting}
      placeholder="Compose a post..."
      editorLabel="Share with your audience"
      buttonLabel="Post"
      enableSuggestions
      enableSendEmailToggle={true}
      showSaveDraftButton
      creatorData={creatorData}
    />
  );
};
